import dispatchActionForAllModules from '@utils/dispatch-action-for-all-modules'
import Vue from 'vue'
import Vuex from 'vuex'
import toast from '@services/toasts/index.js'
import api from '@/plugins/api.storePlugin'
import i18n from '@/plugins/i18n.storePlugin'
import { clearLocalStorageExcept } from '@/helpers/browser-storage'
import modules from './modules'

Vue.use(Vuex)

const getDefaultState = () => {
  return {
    activeLoaders: [],
    loadingCount: 0,
    appLoadingCount: 0,
    debugActivateCounter: 0,
    darkMode: JSON.parse(localStorage.getItem('darkMode')),
    initialAppLoad: false, // Prevents full page loader on subsequent loads
    embedded: false, // App is embedded within RR
    drawer:
      localStorage.getItem('drawer') === undefined ||
      localStorage.getItem('drawer') === null
        ? false
        : JSON.parse(localStorage.getItem('drawer')),
  }
}

const state = getDefaultState()

const store = new Vuex.Store({
  modules,
  state,
  plugins: [api, i18n],
  // Enable strict mode in development to get a warning
  // when mutating state outside of a mutation.
  // https://vuex.vuejs.org/guide/strict.html
  strict: process.env.NODE_ENV !== 'production',
  getters: {
    isLoading: (state) => state.loadingCount > 0,
    isLoadingByKey: (state) => (key) => state.activeLoaders.indexOf(key) > -1,
    isLoadingApp: (state) => state.appLoadingCount > 0,
    hasLoadedAppOnce: (state) => state.initialAppLoad,
    isError: (state) => state.error,
    isDebugModeActive: (state) => state.debugActivateCounter >= 10,
    isDarkModeActive: (state) => state.darkMode,
    isDrawerOpen: (state) => state.drawer,
    isEmbedded: (state) => state.embedded,
  },
  mutations: {
    START_LOADING(state) {
      state.loadingCount++
    },
    FINISH_LOADING(state) {
      state.loadingCount--
    },
    START_LOADING_BY_KEY(state, key) {
      state.activeLoaders.push(key)
    },
    FINISH_LOADING_BY_KEY(state, key) {
      const indexOfLoader = state.activeLoaders.indexOf(key)
      if (indexOfLoader === -1) return
      state.activeLoaders.splice(indexOfLoader, 1)
    },
    INCREMENT_APP_LOADING(state) {
      state.appLoadingCount++
    },
    DECREMENT_APP_LOADING(state) {
      state.appLoadingCount--
    },
    SET_APP_AS_LOADED(state) {
      state.initialAppLoad = true
    },
    RESET_APP_LOADING_STATE(state) {
      state.initialAppLoad = false
    },
    SET_ERROR(state) {
      state.error = true
    },
    ACTIVATE_DEBUG(state) {
      state.debugActivateCounter = 10
    },
    INCREMENT_DEBUG(state) {
      state.debugActivateCounter++
    },
    RESET_DEBUG(state) {
      state.debugActivateCounter = 0
    },
    ENABLE_DARKMODE(state) {
      state.darkMode = true
      localStorage.setItem('darkMode', true)
    },
    DISABLE_DARKMODE(state) {
      state.darkMode = false
      localStorage.setItem('darkMode', false)
    },
    OPEN_DRAWER(state) {
      state.drawer = true
      localStorage.setItem('drawer', true)
    },
    CLOSE_DRAWER(state) {
      state.drawer = false
      localStorage.setItem('drawer', false)
    },
    SET_EMBEDDED(state, value) {
      state.embedded = value
    },
    CLEAR(state) {
      // Clear out local and session storage
      clearLocalStorageExcept(['app.orgTree.presets'])
      sessionStorage.clear()

      Object.assign(state, getDefaultState())
    },
  },
  actions: {
    startLoadingApp({ commit }) {
      commit('INCREMENT_APP_LOADING')
    },
    finishLoadingApp({ commit }) {
      commit('DECREMENT_APP_LOADING')
    },
    startLoading({ commit }) {
      commit('START_LOADING')
    },
    finishLoading({ commit }) {
      commit('FINISH_LOADING')
    },
    setAppAsLoaded({ commit }) {
      commit('SET_APP_AS_LOADED')
    },
    resetAppLoadingState({ commit }) {
      commit('RESET_APP_LOADING_STATE')
    },
    toggleDebugMode({ commit }) {
      if (this.state.debugActivateCounter <= 0) {
        commit('ACTIVATE_DEBUG')
        toast.debug('Debug mode on')
      } else {
        commit('RESET_DEBUG')
        toast.debug('Debug mode off')
      }
    },
    toggleDarkMode({ commit, getters }) {
      getters.isDarkModeActive
        ? commit('DISABLE_DARKMODE')
        : commit('ENABLE_DARKMODE')
    },
    toggleDrawer({ commit }) {
      this.state.drawer ? commit('CLOSE_DRAWER') : commit('OPEN_DRAWER')
    },
    setEmbeddedState({ commit }, value) {
      commit('SET_EMBEDDED', value)
    },
    clearStore({ commit }) {
      dispatchActionForAllModules('clear')

      commit('CLEAR')
    },
    async setLocale(locale) {
      this.$i18n.locale = locale
    },
  },
})

export default store

// Automatically run the `init` action for every module,
// if one exists.
dispatchActionForAllModules('init')
